import './Sobre.scss';
import { useEffect, useRef, useState } from 'react';
import { FaReact, FaNodeJs, FaGitAlt, FaWhatsapp } from 'react-icons/fa';
import { SiPostgresql, SiSass, SiJavascript } from 'react-icons/si';
import { Download, MapPin, Code2 } from 'lucide-react';

// ─── Dados ───────────────────────────────────────────────────────────────────

const STATS = [
  { value: 21, suffix: '+', label: 'Projetos entregues' },
  { value: 4,  suffix: '+', label: 'Anos de experiência' },
  { value: 3,  suffix: '',  label: 'Sistemas em execução' },
];

const TECH_CHIPS = [
  { icon: <FaReact />,      name: 'React',      color: '#61DAFB' },
  { icon: <FaNodeJs />,     name: 'Node.js',    color: '#339933' },
  { icon: <SiJavascript />, name: 'JavaScript', color: '#F7DF1E' },
  { icon: <SiSass />,       name: 'SASS',       color: '#CC6699' },
  { icon: <SiPostgresql />, name: 'PostgreSQL', color: '#336791' },
  { icon: <FaGitAlt />,     name: 'Git',        color: '#F05032' },
  { icon: <FaWhatsapp />,   name: 'Bots',       color: '#25D366' },
];

const INFO_ITEMS = [
  { icon: <MapPin size={15} />, text: 'Rio de Janeiro, RJ' },
  { icon: <Code2 size={15} />,  text: 'Full Stack Developer' },
  { live: true,                 text: 'Aberto a oportunidades' },
];

// ─── Utilitários ─────────────────────────────────────────────────────────────

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// Conta de 0 até o valor quando `run` vira true (easeOutCubic)
function useCountUp(target, run, delay = 0, duration = 1400) {
  const [n, setN] = useState(() => (prefersReducedMotion() ? target : 0));

  useEffect(() => {
    if (!run) return undefined;
    if (prefersReducedMotion()) {
      setN(target);
      return undefined;
    }

    let raf = 0;
    let t0 = 0;
    const step = (t) => {
      if (!t0) t0 = t;
      const p = Math.min((t - t0) / duration, 1);
      setN(Math.round(target * (1 - (1 - p) ** 3)));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    const start = setTimeout(() => { raf = requestAnimationFrame(step); }, delay);

    return () => {
      clearTimeout(start);
      cancelAnimationFrame(raf);
    };
  }, [run, target, delay, duration]);

  return n;
}

// Inclinação 3D da foto seguindo o mouse (só em dispositivos com hover)
function useTilt() {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el || prefersReducedMotion() || !window.matchMedia('(hover: hover)').matches) {
      return undefined;
    }

    let raf = 0;

    const onMove = (e) => {
      const r = el.getBoundingClientRect();
      const x = (e.clientX - r.left) / r.width - 0.5;
      const y = (e.clientY - r.top) / r.height - 0.5;
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        el.style.setProperty('--rx', `${(-y * 14).toFixed(2)}deg`);
        el.style.setProperty('--ry', `${(x * 14).toFixed(2)}deg`);
        el.style.setProperty('--gx', `${((x + 0.5) * 100).toFixed(0)}%`);
        el.style.setProperty('--gy', `${((y + 0.5) * 100).toFixed(0)}%`);
      });
    };

    const reset = () => {
      cancelAnimationFrame(raf);
      el.style.setProperty('--rx', '0deg');
      el.style.setProperty('--ry', '0deg');
    };

    el.addEventListener('pointermove', onMove);
    el.addEventListener('pointerleave', reset);
    return () => {
      cancelAnimationFrame(raf);
      el.removeEventListener('pointermove', onMove);
      el.removeEventListener('pointerleave', reset);
    };
  }, []);

  return ref;
}

// ─── Stat com contagem ───────────────────────────────────────────────────────

function Stat({ value, suffix, label, run, delay }) {
  const n = useCountUp(value, run, delay);

  return (
    <div className="stat-item">
      <span className="stat-value" aria-hidden="true">{n}{suffix}</span>
      <span className="sr-only">{value}{suffix}</span>
      <span className="stat-label">{label}</span>
    </div>
  );
}

// ─── Componente principal ────────────────────────────────────────────────────

function Sobre() {
  const [visible, setVisible] = useState(false);
  const [imgOk, setImgOk] = useState(true);
  const sectionRef = useRef(null);
  const tiltRef = useTilt();

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return undefined;

    if (typeof IntersectionObserver === 'undefined') {
      setVisible(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect(); // só precisa disparar uma vez
        }
      },
      { threshold: 0.12, rootMargin: '0px 0px -8% 0px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="sobre"
      className={`sobre ${visible ? 'is-visible' : ''}`}
      ref={sectionRef}
    >
      <div className="sobre-container">

        {/* Cabeçalho */}
        <div className="section-header" data-reveal>
          <span className="section-eyebrow">Fase 02 · Sobre</span>
          <h2 className="section-title">Sobre Mim</h2>
          <span className="section-bar" aria-hidden="true" />
          <p className="section-sub">
            Desenvolvedor Full Stack apaixonado por criar soluções reais.
          </p>
        </div>

        {/* Layout principal */}
        <div className="sobre-grid">

          {/* Coluna esquerda — foto + info */}
          <aside className="sobre-aside" data-reveal="left" style={{ '--d': '0.1s' }}>
            <div className="perfil-wrap" ref={tiltRef}>
              <span className="perfil-glow" aria-hidden="true" />
              <span className="perfil-ring" aria-hidden="true" />

              {imgOk ? (
                <img
                  src="./assets/danilo-desenho.png"
                  alt="Danilo Braga"
                  className="perfil-img"
                  width="190"
                  height="190"
                  loading="lazy"
                  decoding="async"
                  onError={() => setImgOk(false)}
                />
              ) : (
                <span className="perfil-img perfil-fallback" role="img" aria-label="Danilo Braga">
                  DB
                </span>
              )}

              <span className="perfil-shine" aria-hidden="true" />
            </div>

            <div className="info-list">
              {INFO_ITEMS.map((item) => (
                <div key={item.text} className={`info-item ${item.live ? 'live' : ''}`}>
                  <span className="info-icon">
                    {item.live ? <span className="live-dot" aria-hidden="true" /> : item.icon}
                  </span>
                  <span className="info-text">{item.text}</span>
                </div>
              ))}
            </div>

            <a
              href="./assets/curriculo-danilo.pdf"
              download
              className="btn-curriculo"
              aria-label="Baixar currículo"
            >
              <Download size={15} aria-hidden="true" />
              Baixar currículo
            </a>
          </aside>

          {/* Coluna direita — texto + stats + chips */}
          <div className="sobre-content">

            {/* Texto */}
            <div className="sobre-texto">
              <p data-reveal style={{ '--d': '0.15s' }}>
                Olá! Sou <strong>Danilo Braga</strong>, desenvolvedor{' '}
                <strong>Full Stack</strong> com foco em{' '}
                <strong>React</strong>, <strong>Node.js</strong> e{' '}
                <strong>SASS</strong>. Tenho paixão por criar interfaces modernas,
                responsivas e funcionais, sempre priorizando a melhor experiência
                para o usuário final.
              </p>
              <p data-reveal style={{ '--d': '0.28s' }}>
                Já atuei em sistemas logísticos corporativos, ERPs em produção,
                bots para WhatsApp e Telegram, e plataformas SaaS — sempre com
                atenção à qualidade do código e ao resultado de negócio.
              </p>
              <p data-reveal style={{ '--d': '0.41s' }}>
                Estou aberto a oportunidades, colaborações ou apenas um bom papo
                sobre tecnologia!
              </p>
            </div>

            {/* Stats (contagem animada) */}
            <div className="stats-row" data-reveal style={{ '--d': '0.5s' }}>
              {STATS.map((s, i) => (
                <Stat key={s.label} {...s} run={visible} delay={600 + i * 150} />
              ))}
            </div>

            {/* Tech chips */}
            <div className="tech-chips" data-reveal style={{ '--d': '0.6s' }}>
              {TECH_CHIPS.map((t, i) => (
                <span
                  key={t.name}
                  className="chip"
                  style={{ '--c': t.color, '--i': i }}
                >
                  <span className="chip-icon" style={{ color: t.color }}>{t.icon}</span>
                  {t.name}
                </span>
              ))}
            </div>

          </div>
        </div>

      </div>
    </section>
  );
}

export default Sobre;